import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPortal } from "react-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { hostUI } from "../../../../configuraciones";
import { obtenerCatalogoMaguey } from "../../../../redux/DucksBeneficiario/BitacoraProduccionDucks";
import "../BitacoraEnvasado.css";

// Material UI Icons
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';

const InventarioGranelSection = ({
  granelRecords = [],
  destilacionRecords = [],
  catalogoMaguey = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveGranel,
  handleOpenNewRecord,
  handleEditRecord,
  handleSaveFusion,
  onRegistrarEnvasado
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [claseFilter, setClaseFilter] = useState("todos");

  // Fallback para estado interno si no se pasa setFormData
  const [internalFormData, setInternalFormData] = useState({
    fechaMovimiento: new Date().toISOString().split("T")[0],
    idContenedor: "",
    noLote: "",
    clave_fq: "",
    especie: "",
    categoria: "Mezcal Artesanal",
    clase: "Blanco Joven",
    ingrediente: "",
    litros: "",
    porcentaje_alc_vol: ""
  });

  const activeFormData = (setFormData && formData && Object.keys(formData).length > 0) ? formData : internalFormData;
  const updateFormData = (updater) => {
    if (typeof setFormData === "function") {
      setFormData(updater);
    } else {
      setInternalFormData(updater);
    }
  };

  const dispatch = useDispatch();
  const reduxCatalogo = useSelector(state => state.bitacoraProduccion?.catalogoMaguey || []);
  const [localCatalog, setLocalCatalog] = useState([]);

  // Cargar catálogo de magueyes oficial desde backend o Redux
  useEffect(() => {
    const hasCatalog = (Array.isArray(catalogoMaguey) && catalogoMaguey.length > 0) ||
      (Array.isArray(reduxCatalogo) && reduxCatalogo.length > 0);

    if (!hasCatalog) {
      if (typeof dispatch === "function") {
        dispatch(obtenerCatalogoMaguey());
      }
      axios.get(hostUI + "bitacora-produccion/catalogo-maguey")
        .then(res => {
          const list = res.data?.data || res.data || [];
          if (Array.isArray(list) && list.length > 0) {
            setLocalCatalog(list);
          }
        })
        .catch(err => console.log("Error al cargar catálogo de maguey:", err));
    }
  }, [dispatch, catalogoMaguey, reduxCatalogo]);

  const activeCatalog = useMemo(() => {
    let list = [];
    if (Array.isArray(catalogoMaguey) && catalogoMaguey.length > 0) list = catalogoMaguey;
    else if (Array.isArray(reduxCatalogo) && reduxCatalogo.length > 0) list = reduxCatalogo;
    else if (Array.isArray(localCatalog) && localCatalog.length > 0) list = localCatalog;

    if (list.length === 0) {
      list = [
        { nc: 1, nombre_comun: "MAGUEY ESPADÍN", nombre_cientifico: "Agave angustifolia" },
        { nc: 2, nombre_comun: "MAGUEY TOBALÁ", nombre_cientifico: "Agave potatorum" },
        { nc: 3, nombre_comun: "MAGUEY ARROQUEÑO", nombre_cientifico: "Agave americana" },
        { nc: 4, nombre_comun: "MAGUEY TEPEZTATE", nombre_cientifico: "Agave marmorata" },
        { nc: 5, nombre_comun: "MAGUEY MEXICANO", nombre_cientifico: "Agave rhodacantha" },
        { nc: 6, nombre_comun: "MAGUEY CUISHE", nombre_cientifico: "Agave karwinskii" },
        { nc: 7, nombre_comun: "MAGUEY JABALÍ", nombre_cientifico: "Agave convallis" },
        { nc: 8, nombre_comun: "MAGUEY CHIQUILAO", nombre_cientifico: "Agave seemanniana" }
      ];
    }

    // Deduplicar por combinación única de nombre científico y común
    const uniqueMap = new Map();
    list.forEach(c => {
      const key = `${(c.nombre_cientifico || "").trim()} (${(c.nombre_comun || "").trim()})`.toLowerCase();
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, c);
      }
    });

    // Ordenar alfabéticamente
    return [...uniqueMap.values()].sort((a, b) => {
      const nameA = `${a.nombre_cientifico || ""} ${a.nombre_comun || ""}`.toLowerCase();
      const nameB = `${b.nombre_cientifico || ""} ${b.nombre_comun || ""}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [catalogoMaguey, reduxCatalogo, localCatalog]);

  const formatEspecieLabel = (cat) => {
    if (cat.nombre_cientifico && cat.nombre_comun) {
      return `${cat.nombre_cientifico} (${cat.nombre_comun})`;
    }
    return cat.nombre_cientifico || cat.nombre_comun || "";
  };

  // Modal de Fusión / Mezcla de Graneles
  const [isFusionModalOpen, setIsFusionModalOpen] = useState(false);
  const [fusionSources, setFusionSources] = useState([
    { granelId: "", volExtraer: "" },
    { granelId: "", volExtraer: "" }
  ]);
  const [fusionForm, setFusionForm] = useState({
    fechaMovimiento: new Date().toISOString().split("T")[0],
    noLote: "",
    idContenedor: "",
    categoria: "Mezcal Artesanal",
    clase: "Joven / Blanco (Mezcla)",
    ingredientes: "",
    tipoMaguey: "",
    noAnalisis: "",
    observaciones: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLoteSelect = (e) => {
    const selectedNoLote = e.target.value;
    updateFormData(prev => ({
      ...prev,
      noLote: selectedNoLote,
      no_lote: selectedNoLote
    }));
  };

  // Abrir nuevo registro
  const handleOpenNew = () => {
    if (typeof handleOpenNewRecord === "function") {
      handleOpenNewRecord();
    } else {
      updateFormData({
        id: null,
        fechaMovimiento: new Date().toISOString().split("T")[0],
        fecha: new Date().toISOString().split("T")[0],
        idContenedor: "",
        no_tanque: "",
        noLote: "",
        no_lote: "",
        categoria: "Mezcal Artesanal",
        clase: "Blanco Joven",
        especie: "",
        tipoMaguey: "",
        ingrediente: "",
        ingredientes: "",
        clave_fq: "",
        noAnalisis: "",
        litros: "",
        porcentaje_alc_vol: "",
        invInicialVol: "0",
        invInicialAlc: "0",
        entradaProcedencia: "Destilación",
        entradaVolumen: "0",
        entradaAlc: "0",
        salidaDestino: "",
        salidaVolumen: "0",
        salidaAlc: "0",
        invFinalVol: "0",
        invFinalAlc: "0",
        observaciones: "Ingreso a inventario de mezcal a granel para envasado.",
        status: "borrador"
      });
      setIsFormOpen(true);
    }
  };

  // Editar registro
  const handleEdit = (rec) => {
    if (typeof handleEditRecord === "function") {
      handleEditRecord(rec);
    } else {
      const vol = String(rec.litros ?? rec.invFinalVol ?? rec.entradaVolumen ?? "0").replace(/[^\d.]/g, "");
      const alc = String(rec.porcentaje_alc_vol ?? rec.invFinalAlc ?? rec.entradaAlc ?? "0").replace(/[^\d.]/g, "");
      updateFormData({
        id: rec.id,
        fechaMovimiento: rec.fechaMovimiento || rec.fecha || new Date().toISOString().split("T")[0],
        fecha: rec.fecha || rec.fechaMovimiento || new Date().toISOString().split("T")[0],
        idContenedor: rec.idContenedor || rec.no_tanque || "",
        no_tanque: rec.no_tanque || rec.idContenedor || "",
        noLote: rec.noLote || rec.no_lote || rec.noProduccion || "",
        no_lote: rec.no_lote || rec.noLote || rec.noProduccion || "",
        categoria: rec.categoria || "Mezcal Artesanal",
        clase: rec.clase || "Blanco Joven",
        ingredientes: rec.ingredientes || rec.ingrediente || "",
        ingrediente: rec.ingrediente || rec.ingredientes || "",
        tipoMaguey: rec.tipoMaguey || rec.especie || "",
        especie: rec.especie || rec.tipoMaguey || "",
        noAnalisis: rec.noAnalisisFisicoquimico || rec.noAnalisis || rec.clave_fq || "",
        clave_fq: rec.clave_fq || rec.noAnalisisFisicoquimico || rec.noAnalisis || "",
        litros: vol,
        porcentaje_alc_vol: alc,
        invInicialVol: String(rec.invInicialVol || vol).replace(/[^\d.]/g, ""),
        invInicialAlc: String(rec.invInicialAlc || alc).replace(/[^\d.]/g, ""),
        entradaProcedencia: rec.entradaProcedencia || "",
        entradaVolumen: vol,
        entradaAlc: alc,
        salidaDestino: rec.salidaDestino || "",
        salidaVolumen: String(rec.salidaVolumen || "0").replace(/[^\d.]/g, ""),
        salidaAlc: String(rec.salidaAlc || "0").replace(/[^\d.]/g, ""),
        invFinalVol: vol,
        invFinalAlc: alc,
        observaciones: rec.observaciones || "",
        status: rec.status || "borrador"
      });
      setIsFormOpen(true);
    }
  };

  // Manejo de Fusión / Mezcla
  const handleAddFusionSource = () => {
    setFusionSources(prev => [...prev, { granelId: "", volExtraer: "" }]);
  };

  const handleRemoveFusionSource = (idx) => {
    if (fusionSources.length <= 2) {
      Swal.fire("Atención", "Debe seleccionar al menos 2 lotes origen para realizar una fusión/mezcla.", "warning");
      return;
    }
    setFusionSources(prev => prev.filter((_, i) => i !== idx));
  };

  const handleFusionSourceChange = (idx, field, value) => {
    setFusionSources(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const fusionCalculations = useMemo(() => {
    let totalVolMezcla = 0;
    let totalAlcVolPonderado = 0;
    let ingredientesList = [];
    let magueyesList = [];
    let categoriaBase = "Mezcal Artesanal";
    let claseBase = "Joven / Blanco";

    fusionSources.forEach(src => {
      if (!src.granelId) return;
      const gRecord = (granelRecords || []).find(g => (g.id === src.granelId || g.noLote === src.granelId || g.idContenedor === src.granelId));
      if (gRecord) {
        const vol = parseFloat(src.volExtraer) || 0;
        const alc = parseFloat(String(gRecord.invFinalAlc || gRecord.entradaAlc || 48.5).replace(/[^\d.]/g, "")) || 48.5;
        totalVolMezcla += vol;
        totalAlcVolPonderado += (vol * alc);

        if (gRecord.ingredientes) ingredientesList.push(gRecord.ingredientes);
        if (gRecord.tipoMaguey) magueyesList.push(gRecord.tipoMaguey);
        if (gRecord.categoria) categoriaBase = gRecord.categoria;
        if (gRecord.clase) claseBase = gRecord.clase;
      }
    });

    const alcPonderadoMezcla = totalVolMezcla > 0 ? (totalAlcVolPonderado / totalVolMezcla).toFixed(1) : "48.5";
    const ingredientesUnicos = [...new Set(ingredientesList.filter(Boolean))].join(", ");
    const magueyesUnicos = [...new Set(magueyesList.filter(Boolean))].join(", ");

    return {
      totalVolMezcla,
      alcPonderadoMezcla,
      ingredientesUnicos,
      magueyesUnicos,
      categoriaBase,
      claseBase
    };
  }, [fusionSources, granelRecords]);

  const handleExecuteFusion = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const validSources = fusionSources.filter(s => s.granelId && parseFloat(s.volExtraer) > 0);
    if (validSources.length < 2) {
      Swal.fire("Atención", "Por favor seleccione al menos 2 lotes de granel con volumen a extraer positivo.", "warning");
      return;
    }

    if (!fusionForm.noLote || !fusionForm.idContenedor) {
      Swal.fire("Atención", "Por favor complete el Nº de Lote y el ID Contenedor para el nuevo registro.", "warning");
      return;
    }

    for (const src of validSources) {
      const gRecord = granelRecords.find(g => (g.id === src.granelId || g.noLote === src.granelId || g.idContenedor === src.granelId));
      if (gRecord) {
        const saldoDisp = parseFloat(String(gRecord.invFinalVol || gRecord.entradaVolumen || 0).replace(/[^\d.]/g, "")) || 0;
        const extraido = parseFloat(src.volExtraer) || 0;
        if (extraido > saldoDisp) {
          Swal.fire("Volumen Insuficiente", `El lote ${gRecord.noLote || gRecord.idContenedor} solo tiene ${saldoDisp} L disponibles y se intentó extraer ${extraido} L.`, "error");
          return;
        }
      }
    }

    const { totalVolMezcla, alcPonderadoMezcla, ingredientesUnicos, magueyesUnicos, categoriaBase } = fusionCalculations;

    const newGranelData = {
      id: `GRANEL-MEZCLA-${Date.now().toString().slice(-4)}`,
      fechaMovimiento: fusionForm.fechaMovimiento || new Date().toISOString().split("T")[0],
      noLote: fusionForm.noLote,
      idContenedor: fusionForm.idContenedor,
      categoria: fusionForm.categoria || categoriaBase,
      clase: fusionForm.clase || "Joven / Blanco (Mezcla)",
      ingredientes: fusionForm.ingredientes || ingredientesUnicos,
      tipoMaguey: fusionForm.tipoMaguey || magueyesUnicos || "Espadín",
      noAnalisis: fusionForm.noAnalisis || `ANA-2026-${Math.floor(100 + Math.random() * 900)}`,
      invInicialVol: totalVolMezcla.toString(),
      invInicialAlc: alcPonderadoMezcla,
      entradaProcedencia: `Mezcla/Fusión de ${validSources.length} lotes de granel`,
      entradaVolumen: totalVolMezcla.toString(),
      entradaAlc: alcPonderadoMezcla,
      salidaDestino: "",
      salidaVolumen: "0",
      salidaAlc: "0",
      invFinalVol: totalVolMezcla.toString(),
      invFinalAlc: alcPonderadoMezcla,
      observaciones: fusionForm.observaciones || `Lote creado por mezcla/fusión de ${validSources.length} lotes origen.`,
      status: "aprobado",
      statusUI: "aprobado"
    };

    if (typeof handleSaveFusion === "function") {
      handleSaveFusion(newGranelData, validSources);
    } else if (typeof handleSaveGranel === "function") {
      handleSaveGranel(null, "aprobado", newGranelData);
    }

    setIsFusionModalOpen(false);
    Swal.fire("¡Fusión Realizada!", `Nuevo Lote ${newGranelData.noLote} creado con éxito.`, "success");
  };

  // Filtrado de registros para la tabla
  const filteredRecords = useMemo(() => {
    return (granelRecords || []).filter(rec => {
      const q = search.toLowerCase().trim();
      const matchSearch = !q ||
        (rec.idContenedor && rec.idContenedor.toLowerCase().includes(q)) ||
        (rec.noLote && rec.noLote.toLowerCase().includes(q)) ||
        (rec.clase && rec.clase.toLowerCase().includes(q)) ||
        (rec.categoria && rec.categoria.toLowerCase().includes(q)) ||
        (rec.tipoMaguey && rec.tipoMaguey.toLowerCase().includes(q)) ||
        (rec.materiaPrima && rec.materiaPrima.toLowerCase().includes(q)) ||
        (rec.especie && rec.especie.toLowerCase().includes(q));

      const rawSt = String(rec.status || rec.statusUI || "borrador").toLowerCase();
      let matchStatus = true;
      if (statusFilter === "aprobado") {
        matchStatus = rawSt.includes("aprob") || rawSt.includes("aceptad");
      } else if (statusFilter === "enviada") {
        matchStatus = rawSt.includes("enviad") || rawSt.includes("revis");
      } else if (statusFilter === "borrador") {
        matchStatus = rawSt.includes("borrador");
      } else if (statusFilter === "rechazado") {
        matchStatus = rawSt.includes("rechaz") || rawSt.includes("correc");
      }

      let matchClase = true;
      if (claseFilter !== "todos") {
        const cl = String(rec.clase || "").toLowerCase();
        if (claseFilter === "joven") matchClase = cl.includes("joven") || cl.includes("blanco");
        else if (claseFilter === "reposado") matchClase = cl.includes("reposado");
        else if (claseFilter === "anejo") matchClase = cl.includes("añejo") || cl.includes("anejo");
        else if (claseFilter === "abocado") matchClase = cl.includes("abocado");
        else if (claseFilter === "destilado_con") matchClase = cl.includes("destilado con");
      }

      return matchSearch && matchStatus && matchClase;
    });
  }, [granelRecords, search, statusFilter, claseFilter]);

  return (
    <div className="granel-main-wrapper animate-fadeIn">
      {/* =========================================================================
          1. HEADER Y TOOLBAR DE ACCIONES Y FILTROS
      ========================================================================= */}
      <div className="destilacion-toolbar-container" style={{ marginBottom: "20px" }}>
        <div className="destilacion-toolbar-header">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
              <span className="badge-status enviada" style={{ fontSize: "11px", fontWeight: 700 }}>
                SECCIÓN II: GRANEL
              </span>
            </div>
            <h2 style={{ fontSize: "19px", fontWeight: 800, color: "var(--text-primary)", margin: 0 }}>
              Inventario de Mezcal a Granel
            </h2>
            <small style={{ color: "var(--text-muted)", fontSize: "12.5px" }}>
              Control trazable de contenedores, entradas, salidas y movimientos a granel
            </small>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setIsFusionModalOpen(true)}
              className="btn-cancel-modal"
              style={{
                background: "#F8FAFC",
                color: "#1E293B",
                border: "1px solid #CBD5E1",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                borderRadius: "8px"
              }}
              title="Mezclar o Juntar Graneles en un nuevo lote o contenedor"
            >
              <MergeTypeIcon style={{ fontSize: 18, color: "#059669" }} />
              <span>Mezclar / Juntar Graneles</span>
            </button>

            <button
              type="button"
              onClick={handleOpenNew}
              className="destilacion-btn-new"
              style={{
                background: "linear-gradient(135deg, #059669 0%, #10B981 100%)",
                boxShadow: "0 4px 14px rgba(5, 150, 105, 0.3)"
              }}
            >
              <AddIcon style={{ fontSize: 18 }} />
              <span>Nuevo Registro de Granel</span>
            </button>
          </div>
        </div>

        <div className="destilacion-toolbar-controls">
          {/* Búsqueda */}
          <div className="destilacion-search-box">
            <SearchIcon className="destilacion-search-icon" style={{ color: "#059669" }} />
            <input
              type="text"
              placeholder="Buscar por contenedor, lote, clase, maguey..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="destilacion-search-clear"
                onClick={() => setSearch("")}
                title="Limpiar búsqueda"
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            )}
          </div>

          {/* Filtro por estado */}
          <div className="destilacion-filter-wrapper">
            <FilterListIcon className="destilacion-filter-icon" style={{ color: "#059669" }} />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="destilacion-filter-select"
            >
              <option value="todos">Todos los Estados</option>
              <option value="aprobado">Aceptadas por UI</option>
              <option value="enviada">Enviadas / En Revisión</option>
              <option value="borrador">Borradores</option>
              <option value="rechazado">Rechazadas (Corrección)</option>
            </select>
          </div>

          {/* Filtro por clase */}
          <div className="destilacion-filter-wrapper">
            <LocalDrinkIcon className="destilacion-filter-icon" style={{ color: "#059669" }} />
            <select
              value={claseFilter}
              onChange={e => setClaseFilter(e.target.value)}
              className="destilacion-filter-select"
            >
              <option value="todos">Todas las Clases</option>
              <option value="joven">Joven / Blanco</option>
              <option value="reposado">Reposado</option>
              <option value="anejo">Añejo</option>
              <option value="abocado">Abocado con</option>
              <option value="destilado_con">Destilado con</option>
            </select>
          </div>

          {/* Botón Reset */}
          {(search || statusFilter !== "todos" || claseFilter !== "todos") && (
            <button
              type="button"
              className="destilacion-btn-reset"
              onClick={() => {
                setSearch("");
                setStatusFilter("todos");
                setClaseFilter("todos");
              }}
              title="Restablecer filtros"
            >
              <CloseIcon style={{ fontSize: 15 }} />
              <span>Limpiar Filtros</span>
            </button>
          )}
        </div>
      </div>

      {/* =========================================================================
          2. TABLA HISTÓRICA DE INVENTARIO A GRANEL (EXACTA A CAPTURAS 1 Y 2)
      ========================================================================= */}
      <div className="table-responsive-container">
        <table className="custom-table-bitacora">
          <thead>
            <tr>
              <th>Fecha Mov.</th>
              <th>Contenedor / Lote</th>
              <th>Categoría / Clase</th>
              <th>Materia Prima & Ingredientes</th>
              <th>Nº Análisis</th>
              <th>Saldo Final</th>
              <th>Estatus UI</th>
              <th style={{ textAlign: "center", minWidth: "120px" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-table-cell" style={{ textAlign: "center", padding: "36px", color: "var(--text-muted)" }}>
                  <Inventory2Icon style={{ fontSize: 44, opacity: 0.35, color: "#059669", marginBottom: "8px" }} />
                  <p style={{ margin: 0, fontWeight: 600 }}>No hay registros de inventario a granel disponibles.</p>
                </td>
              </tr>
            ) : (
              filteredRecords.map((rec, idx) => {
                const volFinalNum = parseFloat(String(rec.litros ?? rec.invFinalVol ?? rec.volFinal ?? 0).replace(/[^\d.]/g, "")) || 0;
                const alcFinalNum = parseFloat(String(rec.porcentaje_alc_vol ?? rec.invFinalAlc ?? rec.alcFinal ?? 0).replace(/[^\d.]/g, "")) || 0;

                const matPrima = rec.materiaPrima || rec.tipoMaguey || rec.especie || "-";
                const numAnalisis = rec.noAnalisisFisicoquimico || rec.noAnalisis || rec.clave_fq || "-";
                const estatusRaw = String(rec.status || rec.statusUI || "borrador").toLowerCase();
                const estatusClass = estatusRaw.includes("aprob") ? "aprobado" : estatusRaw.includes("enviad") ? "enviada" : "borrador";

                return (
                  <tr key={rec.id || idx}>
                    <td style={{ fontWeight: 600 }}>{rec.fechaMovimiento || rec.fecha || "-"}</td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong style={{ color: "#059669", fontSize: "13.5px" }}>{rec.idContenedor || rec.no_tanque || "-"}</strong>
                        <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>Lote: {rec.noLote || rec.no_lote || rec.noProduccion || "-"}</span>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong style={{ color: "var(--text-primary)" }}>{rec.categoria || "Mezcal Artesanal"}</strong>
                        <div style={{ fontSize: "11.5px", color: "#64748B" }}>{rec.clase || "Blanco Joven"}</div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: "12.5px" }}>
                        <span>{matPrima}</span>
                        {(rec.ingredientes || rec.ingrediente) && (
                          <div style={{ fontSize: "11px", color: "#7C3AED", fontStyle: "italic" }}>
                            +{rec.ingredientes || rec.ingrediente}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: "12px", fontFamily: "monospace", background: "#F1F5F9", padding: "2px 6px", borderRadius: "6px" }}>
                        {numAnalisis}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong style={{ color: "#059669", fontSize: "14px" }}>{volFinalNum.toLocaleString()} L</strong>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>@ {alcFinalNum}% Alc.</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge-status ${estatusClass}`}>
                        {estatusClass === "aprobado" ? "aprobado" : estatusClass === "enviada" ? "en revisión" : "borrador"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (onRegistrarEnvasado) {
                              onRegistrarEnvasado(rec);
                            }
                          }}
                          title="Registro de envasado de producto"
                          className="btn-action-icon envasar"
                        >
                          <ShoppingBagIcon />
                        </button>

                        <button
                          type="button"
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver Detalle Completo"
                          className="btn-action-icon view"
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleEdit(rec)}
                          title="Editar Identificación del Mezcal y Contenedor"
                          className="btn-action-icon edit"
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* =========================================================================
          3. MODAL DE REGISTRO / EDICIÓN (EXACTO A CAPTURA 3: IDENTIFICACIÓN DEL MEZCAL Y CONTENEDOR)
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "850px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-granel" style={{ background: "linear-gradient(135deg, #065F46 0%, #059669 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div className="detail-modal-badge-row" style={{ marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                    {activeFormData?.id ? "EDITAR IDENTIFICACIÓN" : "NUEVO REGISTRO"}
                  </span>
                  <span className="badge-status enviada" style={{ background: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}>
                    SECCIÓN II: GRANEL
                  </span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  {activeFormData?.id ? `Editar Contenedor: ${activeFormData.idContenedor || activeFormData.noLote || ""}` : "Identificación del Mezcal y Contenedor"}
                </h1>
              </div>

              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="detail-modal-close-btn"
                title="Cerrar ventana"
                aria-label="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Formulario (EXACTO A CAPTURA 3) */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (typeof handleSaveGranel === "function") {
                  handleSaveGranel(e, "aprobado", activeFormData);
                }
              }}
              className="detail-modal-body"
              style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* TARJETA IDENTIFICACIÓN DEL MEZCAL Y CONTENEDOR (CAPTURA 3) */}
              <div className="granel-section-card">
                <div className="granel-section-header">
                  <div className="granel-icon-wrapper-green">
                    <LayersIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="granel-section-header-titles">
                    <h3>Identificación del Mezcal y Contenedor</h3>
                    <p>Selección de lote validado de destilación o ingreso manual</p>
                  </div>
                </div>

                {/* FILA 1 (4 COLUMNAS): Fecha * | Número de Tanque * | No. de Lote * | Clave FQ */}
                <div className="granel-grid-row-4">
                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Fecha <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="date"
                      name="fechaMovimiento"
                      required
                      value={activeFormData?.fechaMovimiento || activeFormData?.fecha || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateFormData(prev => ({ ...prev, fechaMovimiento: val, fecha: val }));
                      }}
                      className="granel-input-control"
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Número de Tanque <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      name="idContenedor"
                      required
                      placeholder="Ej: CLS-05, C"
                      value={activeFormData?.idContenedor || activeFormData?.no_tanque || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateFormData(prev => ({ ...prev, idContenedor: val, no_tanque: val }));
                      }}
                      className="granel-input-control"
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      No. de Lote <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      list="envasado-granel-lotes-list"
                      name="noLote"
                      required
                      placeholder="Ej: AMM-12, A"
                      value={activeFormData?.noLote || activeFormData?.no_lote || ""}
                      onChange={handleLoteSelect}
                      className="granel-input-control"
                    />
                    <datalist id="envasado-granel-lotes-list">
                      {granelRecords.map((r, idx) => (
                        <option key={idx} value={r.noLote || r.noProduccion}>
                          {r.noLote || r.noProduccion} ({r.idContenedor || r.no_tanque || "Tanque"} - {r.invFinalVol || r.litros || 0} L)
                        </option>
                      ))}
                    </datalist>
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Clave FQ
                    </label>
                    <input
                      type="text"
                      name="clave_fq"
                      placeholder="Ej: NNMZ-38"
                      value={activeFormData?.clave_fq || activeFormData?.noAnalisis || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateFormData(prev => ({ ...prev, clave_fq: val, noAnalisis: val }));
                      }}
                      className="granel-input-control"
                    />
                  </div>
                </div>

                {/* FILA 2 (3 COLUMNAS): Especie | Categoría | Clase */}
                <div className="granel-grid-row-3">
                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Especie
                    </label>
                    <select
                      name="especie"
                      value={activeFormData?.especie || activeFormData?.tipoMaguey || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateFormData(prev => ({ ...prev, especie: val, tipoMaguey: val }));
                      }}
                      className="granel-input-control"
                    >
                      <option value="">-- Seleccionar Especie (Ej: Agave angustifolia (MAGUEY ESPADÍN)) --</option>
                      {activeCatalog.map((cat, idx) => {
                        const label = formatEspecieLabel(cat);
                        return (
                          <option key={cat.id_catalogo_maguey || cat.idCatalogoMaguey || cat.nc || idx} value={label}>
                            {label}
                          </option>
                        );
                      })}
                      {activeFormData?.especie && !activeCatalog.some(cat => formatEspecieLabel(cat) === activeFormData.especie) && (
                        <option value={activeFormData.especie}>
                          {activeFormData.especie}
                        </option>
                      )}
                    </select>
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Categoría
                    </label>
                    <select
                      name="categoria"
                      value={activeFormData?.categoria || "Mezcal Artesanal"}
                      onChange={handleChange}
                      className="granel-input-control"
                    >
                      <option value="Mezcal">Mezcal</option>
                      <option value="Mezcal Artesanal">Mezcal Artesanal</option>
                      <option value="Mezcal Ancestral">Mezcal Ancestral</option>
                    </select>
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Clase
                    </label>
                    <select
                      name="clase"
                      value={activeFormData?.clase || "Blanco Joven"}
                      onChange={(e) => {
                        const newClase = e.target.value;
                        updateFormData(prev => ({
                          ...prev,
                          clase: newClase,
                          ingrediente: newClase === "Destilado con" ? prev.ingrediente : "",
                          ingredientes: newClase === "Destilado con" ? prev.ingredientes : ""
                        }));
                      }}
                      className="granel-input-control"
                    >
                      <option value="Blanco Joven">Blanco Joven</option>
                      <option value="Destilado con">Destilado con</option>
                    </select>
                  </div>
                </div>

                {/* FILA 3 (3 COLUMNAS): Ingrediente | Litros * | % Alc. Volumen * */}
                <div className="granel-grid-row-3">
                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Ingrediente
                    </label>
                    <input
                      type="text"
                      name="ingrediente"
                      placeholder="Ej: ---"
                      disabled={activeFormData?.clase !== "Destilado con"}
                      value={activeFormData?.clase === "Destilado con" ? (activeFormData?.ingrediente || activeFormData?.ingredientes || "") : ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateFormData(prev => ({ ...prev, ingrediente: val, ingredientes: val }));
                      }}
                      className="granel-input-control"
                      title={activeFormData?.clase !== "Destilado con" ? "Habilitado únicamente para Destilado con" : ""}
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Litros <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      name="litros"
                      required
                      placeholder="Ej: 2,554"
                      value={activeFormData?.litros ?? (activeFormData?.invFinalVol || activeFormData?.entradaVolumen || "")}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateFormData(prev => ({
                          ...prev,
                          litros: val,
                          invFinalVol: val,
                          entradaVolumen: val,
                          invInicialVol: prev.invInicialVol || val
                        }));
                      }}
                      className="granel-input-control"
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      % Alc. Volumen <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      max="100"
                      name="porcentaje_alc_vol"
                      required
                      placeholder="Ej: 50.26"
                      value={activeFormData?.porcentaje_alc_vol ?? (activeFormData?.invFinalAlc || activeFormData?.entradaAlc || "")}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateFormData(prev => ({
                          ...prev,
                          porcentaje_alc_vol: val,
                          invFinalAlc: val,
                          entradaAlc: val,
                          invInicialAlc: prev.invInicialAlc || val
                        }));
                      }}
                      className="granel-input-control"
                    />
                  </div>
                </div>
              </div>

              {/* Footer de Acciones del Modal */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #E2E8F0" }}>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="btn-cancel-modal"
                >
                  Cancelar
                </button>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      if (typeof handleSaveGranel === "function") {
                        handleSaveGranel(e, "borrador", activeFormData);
                      }
                    }}
                    className="btn-cancel-modal"
                    style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #CBD5E1", fontWeight: 700 }}
                  >
                    Guardar Borrador
                  </button>

                  <button
                    type="submit"
                    className="destilacion-btn-new"
                    style={{ background: "linear-gradient(135deg, #059669 0%, #10B981 100%)", boxShadow: "0 4px 14px rgba(5, 150, 105, 0.3)" }}
                  >
                    <CheckCircleIcon style={{ fontSize: 18 }} />
                    <span>{activeFormData?.id ? "Guardar Cambios" : "Registrar Movimiento"}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          4. MODAL DETALLE COMPLETO (VISUALIZACIÓN CON ICONO DE OJO)
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setSelectedRecord(null)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "800px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-granel" style={{ background: "linear-gradient(135deg, #065F46 0%, #059669 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div className="detail-modal-badge-row" style={{ marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                    DETALLE DE MOVIMIENTO A GRANEL
                  </span>
                  <span className="badge-status enviada" style={{ background: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}>
                    {selectedRecord.statusUI || selectedRecord.status || "Aprobado"}
                  </span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Lote: {selectedRecord.noLote || selectedRecord.no_lote || selectedRecord.noProduccion || "Sin Lote"}
                </h1>
              </div>

              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="detail-modal-close-btn"
                title="Cerrar ventana"
                aria-label="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="detail-modal-body" style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Tarjeta Identificación */}
              <div className="granel-section-card">
                <div className="granel-section-header">
                  <div className="granel-icon-wrapper-green">
                    <LayersIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="granel-section-header-titles">
                    <h3>Identificación del Mezcal y Contenedor</h3>
                    <p>Datos registrados para custodia y envasado</p>
                  </div>
                </div>

                <div className="granel-grid-row-4">
                  <div className="granel-input-group">
                    <label className="granel-input-label">FECHA MOVIMIENTO</label>
                    <input type="text" readOnly value={selectedRecord.fechaMovimiento || selectedRecord.fecha || "-"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">NÚMERO DE TANQUE</label>
                    <input type="text" readOnly value={selectedRecord.idContenedor || selectedRecord.no_tanque || "-"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">NO. DE LOTE</label>
                    <input type="text" readOnly value={selectedRecord.noLote || selectedRecord.no_lote || selectedRecord.noProduccion || "-"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">CLAVE FQ / ANÁLISIS</label>
                    <input type="text" readOnly value={selectedRecord.noAnalisisFisicoquimico || selectedRecord.noAnalisis || selectedRecord.clave_fq || "-"} className="granel-input-control" />
                  </div>
                </div>

                <div className="granel-grid-row-3" style={{ marginTop: "12px" }}>
                  <div className="granel-input-group">
                    <label className="granel-input-label">ESPECIE / MATERIA PRIMA</label>
                    <input type="text" readOnly value={selectedRecord.materiaPrima || selectedRecord.tipoMaguey || selectedRecord.especie || "-"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">CATEGORÍA</label>
                    <input type="text" readOnly value={selectedRecord.categoria || "Mezcal Artesanal"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">CLASE</label>
                    <input type="text" readOnly value={selectedRecord.clase || "Blanco Joven"} className="granel-input-control" />
                  </div>
                </div>

                <div className="granel-grid-row-3" style={{ marginTop: "12px" }}>
                  <div className="granel-input-group">
                    <label className="granel-input-label">INGREDIENTE</label>
                    <input type="text" readOnly value={selectedRecord.ingredientes || selectedRecord.ingrediente || "-"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">LITROS (SALDO FINAL)</label>
                    <input
                      type="text"
                      readOnly
                      value={`${parseFloat(String(selectedRecord.litros ?? selectedRecord.invFinalVol ?? 0).replace(/[^\d.]/g, "") || 0).toLocaleString()} L`}
                      className="granel-input-control"
                      style={{ fontWeight: 800, color: "#059669" }}
                    />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">% ALC. VOLUMEN</label>
                    <input
                      type="text"
                      readOnly
                      value={`${parseFloat(String(selectedRecord.porcentaje_alc_vol ?? selectedRecord.invFinalAlc ?? 0).replace(/[^\d.]/g, "") || 0)} % Alc.`}
                      className="granel-input-control"
                      style={{ fontWeight: 800, color: "#059669" }}
                    />
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              {selectedRecord.observaciones && (
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "12px 16px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: "#64748B", display: "block", marginBottom: "4px" }}>OBSERVACIONES:</span>
                  <p style={{ margin: 0, fontSize: "13px", color: "#1E293B" }}>{selectedRecord.observaciones}</p>
                </div>
              )}

              {/* Footer */}
              <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "12px", borderTop: "1px solid #E2E8F0" }}>
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  className="btn-cancel-modal"
                  style={{ background: "#1E293B", color: "white", padding: "8px 20px" }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          5. MODAL DE FUSIÓN / MEZCLA DE GRANELES
      ========================================================================= */}
      {isFusionModalOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFusionModalOpen(false)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "850px" }}
          >
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div className="detail-modal-badge-row" style={{ marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                    PROCESO DE MEZCLA / ENSAMBLE
                  </span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Fusión / Juntar Lotes de Granel
                </h1>
              </div>

              <button
                type="button"
                onClick={() => setIsFusionModalOpen(false)}
                className="detail-modal-close-btn"
                title="Cerrar ventana"
                aria-label="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={handleExecuteFusion} className="detail-modal-body" style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div className="granel-section-card">
                <div className="granel-section-header">
                  <div className="granel-icon-wrapper-copper">
                    <MergeTypeIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="granel-section-header-titles">
                    <h3>Selección de Lotes Origen a Combinar</h3>
                    <p>Seleccione al menos dos contenedores/lotes e indique el volumen en litros a extraer de cada uno</p>
                  </div>
                </div>

                {fusionSources.map((source, idx) => {
                  const selectedGranel = granelRecords.find(g => (g.id === source.granelId || g.noLote === source.granelId || g.idContenedor === source.granelId));
                  const saldoMax = selectedGranel ? parseFloat(String(selectedGranel.invFinalVol || selectedGranel.litros || 0).replace(/[^\d.]/g, "")) || 0 : 0;

                  return (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr auto", gap: "12px", alignItems: "flex-end", marginBottom: "12px" }}>
                      <div className="granel-input-group">
                        <label className="granel-input-label">LOTE / CONTENEDOR ORIGEN #{idx + 1} *</label>
                        <select
                          required
                          value={source.granelId}
                          onChange={e => handleFusionSourceChange(idx, "granelId", e.target.value)}
                          className="granel-input-control"
                        >
                          <option value="">-- Seleccionar Lote de Granel --</option>
                          {granelRecords.map((g, gIdx) => (
                            <option key={g.id || gIdx} value={g.id || g.noLote}>
                              {g.idContenedor || g.no_tanque || "Tanque"} | Lote: {g.noLote || g.noProduccion} | Saldo: {g.invFinalVol || g.litros || 0} L (@ {g.invFinalAlc || g.porcentaje_alc_vol || 0}% Alc.)
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="granel-input-group">
                        <label className="granel-input-label">VOLUMEN A EXTRAER (L) *</label>
                        <input
                          type="number"
                          step="any"
                          min="0.1"
                          max={saldoMax || undefined}
                          required
                          placeholder={saldoMax ? `Máx: ${saldoMax} L` : "0.0"}
                          value={source.volExtraer}
                          onChange={e => handleFusionSourceChange(idx, "volExtraer", e.target.value)}
                          className="granel-input-control"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveFusionSource(idx)}
                        className="btn-action-icon delete"
                        style={{ height: "42px", width: "42px", borderRadius: "8px" }}
                        title="Eliminar este lote origen"
                      >
                        <DeleteIcon style={{ fontSize: 18 }} />
                      </button>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={handleAddFusionSource}
                  className="btn-cancel-modal"
                  style={{ background: "#F1F5F9", color: "#059669", border: "1px dashed #059669", fontWeight: 700, width: "100%", marginTop: "6px" }}
                >
                  + Agregar Otro Lote Origen a la Fusión
                </button>
              </div>

              {/* Cálculo en vivo de la Fusión */}
              <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: "10px", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontSize: "12px", fontWeight: 800, color: "#065F46", textTransform: "uppercase" }}>Resultado Estimado de Mezcla</span>
                  <div style={{ fontSize: "13px", color: "#047857", marginTop: "2px" }}>
                    Categoría resultante: <strong>{fusionCalculations.categoriaBase}</strong> | Clase: <strong>{fusionCalculations.claseBase}</strong>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "20px", fontWeight: 800, color: "#059669" }}>
                    {fusionCalculations.totalVolMezcla.toLocaleString()} L
                  </div>
                  <span style={{ fontSize: "12px", color: "#065F46", fontWeight: 700 }}>
                    @ {fusionCalculations.alcPonderadoMezcla}% Alc. Vol. (Ponderado)
                  </span>
                </div>
              </div>

              {/* Datos del Nuevo Registro Resultante */}
              <div className="granel-section-card" style={{ marginBottom: 0 }}>
                <div className="granel-section-header">
                  <div className="granel-icon-wrapper-green">
                    <LayersIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="granel-section-header-titles">
                    <h3>Identificación del Mezcal y Contenedor Resultante</h3>
                    <p>Datos principales del nuevo lote de mezcla</p>
                  </div>
                </div>

                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">FECHA MOVIMIENTO *</label>
                    <input
                      type="date"
                      required
                      value={fusionForm.fechaMovimiento}
                      onChange={e => setFusionForm(prev => ({ ...prev, fechaMovimiento: e.target.value }))}
                      className="granel-input-control"
                    />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">Nº DE LOTE RESULTANTE *</label>
                    <input
                      type="text"
                      required
                      placeholder="Ej: LOTE-MEZCLA-01"
                      value={fusionForm.noLote}
                      onChange={e => setFusionForm(prev => ({ ...prev, noLote: e.target.value }))}
                      className="granel-input-control"
                    />
                  </div>
                </div>

                <div className="granel-grid-row-2" style={{ marginTop: "12px" }}>
                  <div className="granel-input-group">
                    <label className="granel-input-label">ID CONTENEDOR DESTINO *</label>
                    <input
                      type="text"
                      required
                      placeholder="Ej: TANQUE-01 / GARRAFA-05"
                      value={fusionForm.idContenedor}
                      onChange={e => setFusionForm(prev => ({ ...prev, idContenedor: e.target.value }))}
                      className="granel-input-control"
                    />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">CATEGORÍA</label>
                    <select
                      value={fusionForm.categoria}
                      onChange={e => setFusionForm(prev => ({ ...prev, categoria: e.target.value }))}
                      className="granel-input-control"
                    >
                      <option value="Mezcal">Mezcal</option>
                      <option value="Mezcal Artesanal">Mezcal Artesanal</option>
                      <option value="Mezcal Ancestral">Mezcal Ancestral</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #E2E8F0" }}>
                <button
                  type="button"
                  onClick={() => setIsFusionModalOpen(false)}
                  className="btn-cancel-modal"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="destilacion-btn-new"
                  style={{ background: "linear-gradient(135deg, #059669 0%, #10B981 100%)" }}
                >
                  <CheckCircleIcon style={{ fontSize: 18 }} />
                  <span>Confirmar Fusión y Registrar</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InventarioGranelSection;
